import React, { useState, useMemo } from 'react';
import { VehicleRow, getStatusIcon, getStatusColor, getStatusLabel, getStatusBackground } from '../types';
import DetailsModal from './DetailsModal';
import '../styles/DataTable.css';

interface DataTableProps {
  vehicles: VehicleRow[];
  isProcessing: boolean;
  onProcess: () => void;
  onDownload: () => void;
  onReset: () => void;
  onEditVehicle: (index: number, field: 'vatNumber' | 'licensePlate', value: string) => void;
  onAddRow: () => void;
  onDeleteRow: (index: number) => void;
  duplicateInfo?: { removed: number; total: number } | null;
}

const DataTable: React.FC<DataTableProps> = ({
  vehicles,
  isProcessing,
  onProcess,
  onDownload,
  onReset,
  onEditVehicle,
  onAddRow,
  onDeleteRow,
  duplicateInfo
}) => {
  const hasResults = vehicles.some(v => v.status && v.status.trim() !== '');
  const [selectedMessage, setSelectedMessage] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [editingCell, setEditingCell] = useState<{ index: number; field: 'vatNumber' | 'licensePlate' } | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  
  // Column visibility: hide result columns before processing
  const showResultColumns = hasResults || isProcessing;
  
  const handleStartEdit = (index: number, field: 'vatNumber' | 'licensePlate', currentValue: string) => {
    // Only allow editing if vehicle hasn't been processed
    if (vehicles[index]?.status) {
      return;
    }
    setEditingCell({ index, field });
    setEditValue(currentValue);
  };
  
  const handleSaveEdit = () => {
    if (editingCell) {
      onEditVehicle(editingCell.index, editingCell.field, editValue.trim());
      setEditingCell(null);
      setEditValue('');
    }
  };
  
  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };
  
  // Check if there are any unprocessed vehicles available
  const hasUnprocessedVehicles = vehicles.some(v => !v.status);

  // Helper function to render status cell
  const renderStatusCell = (vehicle: VehicleRow, isProcessing: boolean) => {
    if (isProcessing && !vehicle.status) {
      return (
        <div className="shimmer-cell">
          <div className="shimmer-content"></div>
        </div>
      );
    }
    
    if (vehicle.status && vehicle.status.trim() !== '') {
      return (
        <span 
          className="status-badge" 
          style={{ 
            background: getStatusBackground(vehicle.status),
            color: getStatusColor(vehicle.status),
            borderColor: getStatusColor(vehicle.status)
          }}
        >
          <span className="status-icon">{getStatusIcon(vehicle.status)}</span>
          <span className="status-text">{getStatusLabel(vehicle.status)}</span>
        </span>
      );
    }
    
    return (
      <span className="status-badge pending">
        <span className="status-icon">{getStatusIcon()}</span>
        <span className="status-text">Εκκρεμεί</span>
      </span>
    );
  };

  // Helper function to render data cell with shimmer
  const renderDataCell = (value: string | undefined, isProcessing: boolean, fieldValue: string | undefined) => {
    if (isProcessing && !fieldValue) {
      return (
        <div className="shimmer-cell">
          <div className="shimmer-content"></div>
        </div>
      );
    }
    
    return value && value.trim() !== '' ? value : '-';
  };

  // Helper function to render message cell
  const renderMessageCell = (vehicle: VehicleRow, isProcessing: boolean) => {
    if (isProcessing && !vehicle.message) {
      return (
        <div className="shimmer-cell">
          <div className="shimmer-content"></div>
        </div>
      );
    }
    
    if (vehicle.message) {
      return (
        <button 
          className="view-details-btn"
          onClick={() => setSelectedMessage(vehicle.message || '')}
          title="Προβολή λεπτομερειών"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
          <span>Προβολή</span>
        </button>
      );
    }
    
    return '-';
  };

  // Generate stable keys for vehicles based on their data
  const vehicleKeys = useMemo(() => {
    const seenKeys = new Set<string>();
    let fallbackCounter = 0;
    
    return vehicles.map(vehicle => {
      const vatNum = vehicle.vatNumber?.trim() || '';
      const license = vehicle.licensePlate?.trim() || '';
      
      if (vatNum && license) {
        const key = `${vatNum}-${license}`;
        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          return key;
        }
      }
      
      // For vehicles without both identifiers or duplicates, use a hash of all available data
      const dataString = `${vatNum}-${license}-${vehicle.status || ''}-${vehicle.message || ''}-${vehicle.searchDate || ''}`;
      let hash = 0;
      for (let i = 0; i < dataString.length; i++) {
        const char = dataString.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
      
      const hashKey = `vehicle-${Math.abs(hash)}-${fallbackCounter++}`;
      seenKeys.add(hashKey);
      return hashKey;
    });
  }, [vehicles]);

  return (
    <>
      {duplicateInfo && duplicateInfo.removed > 0 && (
        <div className="duplicate-notification">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
          <span>
            <strong>Εντοπίστηκαν διπλότυπα:</strong> Αφαιρέθηκαν <strong>{duplicateInfo.removed}</strong> διπλότυπες εγγραφές (ίδιο ΑΦΜ και Αριθμό Κυκλοφορίας) από {duplicateInfo.total} συνολικές εγγραφές. Εμφανίζονται {vehicles.length} μοναδικές εγγραφές.
          </span>
        </div>
      )}
      <div className={`data-table-container ${isExpanded ? 'expanded' : ''}`}>
        <button 
          className="expand-toggle-btn"
          onClick={() => setIsExpanded(!isExpanded)}
          title={isExpanded ? "Συμπίεση" : "Επέκταση"}
        >
          {isExpanded ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M4 8h16M4 16h16"/>
            </svg>
          )}
          <span className="expand-btn-text">{isExpanded ? 'Συμπίεση' : 'Επέκταση'}</span>
        </button>
      <div className="table-header">
        <div className="table-info">
          <h2>Δεδομένα Ασφάλισης Οχημάτων</h2>
          <p>
            {vehicles.length} όχημα{vehicles.length !== 1 ? 'τα' : ''} φορτώθηκαν
          </p>
        </div>
          <div className="table-header-right">
        <div className="table-actions">
              {hasUnprocessedVehicles && !isProcessing && (
            <>
                <button 
                    className="action-btn secondary add-row-btn"
                    onClick={onAddRow}
                    title="Προσθήκη Νέας Εγγραφής"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                    Προσθήκη Εγγραφής
                </button>
              <button 
                    className="action-btn primary process-start-btn" 
                onClick={onProcess}
                    disabled={!hasUnprocessedVehicles}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 11l3 3L22 4"/>
                  <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
                </svg>
                    Έλεγχος Ασφάλισης
              </button>
            </>
          )}
          {hasResults && (
            <>
              <button className="action-btn success" onClick={onDownload}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Λήψη Αποτελεσμάτων
              </button>
            </>
          )}
            </div>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>#</th>
              <th>ΑΦΜ</th>
              <th>Αρ. Κυκλοφορίας</th>
              <th>Κατάσταση</th>
              <th className={hasUnprocessedVehicles ? '' : 'hidden-column'}>Ενέργειες</th>
              <th className="hidden-column">Ημ/νία Αναζήτησης</th>
              <th className={showResultColumns ? '' : 'hidden-column'}>Τύπος Οχήματος</th>
              <th className={showResultColumns ? '' : 'hidden-column'}>Ασφαλιστική Εταιρία</th>
              <th className={showResultColumns ? '' : 'hidden-column'}>Έναρξη Κάλυψης</th>
              <th className={showResultColumns ? '' : 'hidden-column'}>Λήξη Κάλυψης</th>
              <th className={showResultColumns ? '' : 'hidden-column'}>Λεπτομέρειες</th>
            </tr>
          </thead>
          <tbody>
            {vehicles.map((vehicle, index) => {
              const vehicleKey = vehicleKeys[index];
              
              return (
              <tr 
                key={vehicleKey}
                className="fade-in"
              >
                <td className="row-number">{index + 1}</td>
                <td className="editable-cell">
                  {editingCell?.index === index && editingCell?.field === 'vatNumber' ? (
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={handleSaveEdit}
                      onKeyDown={handleKeyDown}
                      className="edit-input"
                      autoFocus
                    />
                  ) : (
                    <span 
                      onClick={() => handleStartEdit(index, 'vatNumber', vehicle.vatNumber)}
                      className={!vehicle.status ? 'editable-field' : ''}
                      title={!vehicle.status ? 'Κάντε κλικ για επεξεργασία' : ''}
              >
                      {vehicle.vatNumber || <span className="empty-field">-</span>}
                    </span>
                  )}
                </td>
                <td className="editable-cell">
                  {editingCell?.index === index && editingCell?.field === 'licensePlate' ? (
                  <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={handleSaveEdit}
                      onKeyDown={handleKeyDown}
                      className="edit-input"
                      autoFocus
                    />
                  ) : (
                    <span 
                      onClick={() => handleStartEdit(index, 'licensePlate', vehicle.licensePlate)}
                      className={!vehicle.status ? 'editable-field license-plate' : 'license-plate'}
                      title={!vehicle.status ? 'Κάντε κλικ για επεξεργασία' : ''}
                    >
                      {vehicle.licensePlate || <span className="empty-field">-</span>}
                    </span>
                  )}
                </td>
                <td>
                  {renderStatusCell(vehicle, isProcessing)}
                </td>
                <td className={`actions-cell ${vehicle.status ? 'hidden-column' : ''}`}>
                  {!vehicle.status && !isProcessing && (
                    <button
                      className="delete-row-btn"
                      onClick={() => onDeleteRow(index)}
                      title="Διαγραφή γραμμής"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                      </svg>
                    </button>
                  )}
                </td>
                <td className="hidden-column">
                  {renderDataCell(vehicle.searchDate, isProcessing, vehicle.searchDate)}
                </td>
                <td className={showResultColumns ? '' : 'hidden-column'}>
                  {renderDataCell(vehicle.vehicleType, isProcessing, vehicle.vehicleType)}
                </td>
                <td className={showResultColumns ? '' : 'hidden-column'}>
                  {renderDataCell(vehicle.insuranceCompany, isProcessing, vehicle.insuranceCompany)}
                </td>
                <td className={showResultColumns ? '' : 'hidden-column'}>
                  {renderDataCell(vehicle.coverageStart, isProcessing, vehicle.coverageStart)}
                </td>
                <td className={showResultColumns ? '' : 'hidden-column'}>
                  {renderDataCell(vehicle.coverageEnd, isProcessing, vehicle.coverageEnd)}
                </td>
                <td className={`message-cell ${showResultColumns ? '' : 'hidden-column'}`}>
                  {renderMessageCell(vehicle, isProcessing)}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {isProcessing && (
        <div className="processing-overlay">
          <div className="processing-spinner">
            <div className="spinner"></div>
            <p>Επεξεργασία οχημάτων...</p>
            <span className="processing-note">Αυτό μπορεί να πάρει λίγα λεπτά</span>
          </div>
        </div>
      )}

      {selectedMessage && (
        <DetailsModal 
          message={selectedMessage} 
          onClose={() => setSelectedMessage(null)} 
        />
      )}
    </div>
    </>
  );
};

export default DataTable;
